import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Client, ClientCompanyInfo, ClientIdentity, ClientPreferences, ClientNote, ClientEmail, ClientTask, Trip } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  Building2, 
  User, 
  CreditCard, 
  Ruler, 
  FileText, 
  CheckSquare,
  ArrowLeft,
  Users,
  Luggage
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLocation } from "wouter";

// Import contact panels
import { PersonalInfoPanel } from "@/components/clients/PersonalInfoPanel";
import { RelationshipPanel } from "@/components/clients/RelationshipPanel";
import { IdentityInfoPanel } from "@/components/clients/IdentityInfoPanel";
import { PreferencesPanel } from "@/components/clients/PreferencesPanel";
import { TripsPanel } from "@/components/clients/TripsPanel";
import { NotesPanel } from "@/components/clients/NotesPanel";
import { EmailsPanel } from "@/components/clients/EmailsPanel";
import { TasksPanel } from "@/components/clients/TasksPanel";
import { InlineEditableField } from "@/components/InlineEditableField";
import { useGravatar } from "@/hooks/useGravatar";
// (Select already imported above)

const getContactInitials = (contact: Client) => {
  const firstName = contact.firstName || '';
  const lastName = contact.lastName || '';
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'UN';
};

const getContactFullName = (contact: Client) => {
  const firstName = contact.firstName || '';
  const lastName = contact.lastName || '';
  const preferredName = contact.preferredName;

  if (preferredName) {
    return `${preferredName} ${lastName}`.trim();
  }

  return `${firstName} ${lastName}`.trim() || 'Unknown Contact';
};

const getContactCompany = (contact: Client): string | null => {
  const record = contact as Record<string, unknown>;

  if (typeof record.company === 'string' && record.company.trim()) {
    return record.company.trim();
  }

  if (typeof record.companyName === 'string' && record.companyName.trim()) {
    return record.companyName.trim();
  }

  const companyInfo = record.companyInfo;
  if (companyInfo && typeof companyInfo === 'object' && !Array.isArray(companyInfo)) {
    const companyName = (companyInfo as Record<string, unknown>).companyName;
    if (typeof companyName === 'string' && companyName.trim()) {
      return companyName.trim();
    }
  }

  return null;
};

const isContactActive = (contact: Client): boolean => {
  const record = contact as Record<string, unknown>;

  if (typeof record.isActive === 'boolean') {
    return record.isActive;
  }

  if (typeof record.status === 'string') {
    return record.status.toLowerCase() !== 'inactive';
  }

  return true;
};

// Contact List Component
const ContactsList: React.FC<{
  onContactSelect: (contact: Client) => void;
  selectedContactId?: number;
  isActive?: boolean;
}> = ({ onContactSelect, selectedContactId, isActive = true }) => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: contactsResponse, isLoading, error } = useQuery<{ contacts: Client[], total: number } | Client[]>({
    queryKey: ['/api/clients', { page: currentPage, pageSize, search: searchTerm, filter: filterBy }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(filterBy !== 'all' && { filter: filterBy })
      });
      
      const response = await fetch(`/api/clients?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }
      
      const data = await response.json();
      return data;
    }
  });

  // Handle different API response formats
  const contacts = Array.isArray(contactsResponse) 
    ? contactsResponse 
    : contactsResponse?.contacts || [];

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load contacts</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Check if contacts data is not in expected format
  if (contactsResponse && !isLoading && !Array.isArray(contacts)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Invalid contacts data format</p>
          <p className="text-gray-600 text-sm mb-4">
            Expected array but received: {typeof contactsResponse}
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Clients</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage your client relationships</p>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterBy} onValueChange={setFilterBy}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
            <SelectItem value="leads">Leads</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contact List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-60" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(contacts) && contacts.map((contact, index) => {
                  const companyName = getContactCompany(contact);
                  const active = isContactActive(contact);

                  return (
                    <TableRow
                      key={contact.id}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        selectedContactId === contact.id ? 'bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500' : ''
                      }`}
                      onClick={() => onContactSelect(contact)}
                    >
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-orange-100 text-orange-700 text-xs">
                              {getContactInitials(contact)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {getContactFullName(contact)}
                          </p>
                          {contact.preferredName && contact.preferredName !== contact.firstName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              ({contact.firstName} {contact.lastName})
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <PrimaryEmailCell contactId={contact.id} index={index} />
                    <PrimaryPhoneCell contactId={contact.id} index={index} />
                    <TableCell>
                      <div className="flex items-center text-gray-600 dark:text-gray-300">
                        <Building2 className="h-3 w-3 mr-2" />
                        {companyName ?? 'No company'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={active ? 'default' : 'secondary'}>
                        {active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {Array.isArray(contacts) && contacts.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No clients found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first client.'}
          </p>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      )}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Client</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="block text-xs text-gray-600 mb-1">First Name</label>
              <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="First name" />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-600 mb-1">Last Name</label>
              <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Last name" />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-600 mb-1">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-600 mb-1">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
            </div>
          </div>
          {errorMsg && <p className="text-sm text-red-600 mt-2">{errorMsg}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700"
              disabled={creating}
              onClick={async () => {
                setErrorMsg(null);
                if (!form.firstName.trim() || !form.lastName.trim()) {
                  setErrorMsg('First and last name are required.');
                  return;
                }
                try {
                  setCreating(true);
                  const resp = await fetch('/api/clients', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                      firstName: form.firstName.trim(),
                      lastName: form.lastName.trim(),
                      email: form.email.trim() || undefined,
                      phone: form.phone.trim() || undefined,
                    })
                  });
                  if (!resp.ok) throw new Error('Failed to create client');
                  const created = await resp.json();
                  setAddOpen(false);
                  setForm({ firstName: '', lastName: '', email: '', phone: '' });
                  await queryClient.invalidateQueries({ queryKey: ['/api/clients'] });
                  if (created && created.id) {
                    onContactSelect(created);
                  }
                } catch (e: any) {
                  setErrorMsg(e?.message || 'Failed to create client');
                } finally {
                  setCreating(false);
                }
              }}
            >
              {creating ? 'Creating…' : 'Create Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Contact Detail Component
const ContactDetail: React.FC<{
  contact: Client;
  onBack: () => void;
  onContactChange: (updates: Partial<Client>) => void;
}> = ({ contact, onBack, onContactChange }) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [editMode, setEditMode] = useState(false);
  const [originalContact, setOriginalContact] = useState<Client | null>(null);
  const [draft, setDraft] = useState<Partial<Client>>({});
  const [companyDraft, setCompanyDraft] = useState<any>({});
  const [preferencesDraft, setPreferencesDraft] = useState<any>({});
  const [identitiesDraft, setIdentitiesDraft] = useState<Record<number, any>>({});
  const [methodsDraft, setMethodsDraft] = useState<{
    phones: { id?: number; type: 'Mobile'|'Work'|'Home'; value: string }[];
    emails: { id?: number; type: 'Personal'|'Work'; value: string }[];
    primaryPhoneIndex: number;
    primaryEmailIndex: number;
  } | null>(null);
  const [savingAll, setSavingAll] = useState(false);

  const companyName = getContactCompany(contact);
  const active = isContactActive(contact);
  

  // Fetch related data for the contact
  const { avatarUrl: gravatarUrl } = useGravatar(contact.email || '');

  const { data: companyInfo } = useQuery<ClientCompanyInfo[]>({
    queryKey: [`/api/clients/${contact.id}/company`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/company`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: identity } = useQuery<ClientIdentity[]>({
    queryKey: [`/api/clients/${contact.id}/identities`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/identities`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: preferences } = useQuery<ClientPreferences[]>({
    queryKey: [`/api/clients/${contact.id}/preferences`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/preferences`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: trips } = useQuery<Trip[]>({
    queryKey: [`/api/clients/${contact.id}/trips`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/trips`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  // Primary methods for header
  const { data: headerEmails } = useQuery<any[]>({
    queryKey: [`/api/clients/${contact.id}/contact-methods`, 'email', 'header'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contact.id}/contact-methods?kind=email`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      return resp.ok ? resp.json() : [];
    }
  });
  const { data: headerPhones } = useQuery<any[]>({
    queryKey: [`/api/clients/${contact.id}/contact-methods`, 'phone', 'header'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contact.id}/contact-methods?kind=phone`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      return resp.ok ? resp.json() : [];
    }
  });
  const primaryEmail = (headerEmails || []).find((m:any)=> m.isPrimary)?.value || (headerEmails || [])[0]?.value;
  const primaryPhoneRaw = (headerPhones || []).find((m:any)=> m.isPrimary)?.value || (headerPhones || [])[0]?.value;

  // Format header phone as (###) ###-#### x###
  const splitPhoneAndExt = (raw: string) => {
    const s = (raw || '').trim();
    const m = s.match(/^(.*?)(?:\s*(?:ext\.?|x)\s*(\d+))$/i);
    if (m) return { base: m[1] || '', ext: m[2] };
    return { base: s };
  };
  const formatDigitsToUS = (digits: string) => {
    const d = (digits || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };
  const formatUSPhone = (val: string) => {
    const { base, ext } = splitPhoneAndExt(val || '');
    const formatted = formatDigitsToUS(base);
    return ext ? `${formatted}  x${ext}` : formatted;
  };
  const primaryPhone = primaryPhoneRaw ? formatUSPhone(primaryPhoneRaw) : undefined;

  const { data: notes } = useQuery<ClientNote[]>({
    queryKey: [`/api/clients/${contact.id}/notes`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/notes`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: emails } = useQuery<ClientEmail[]>({
    queryKey: [`/api/clients/${contact.id}/emails`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/emails`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: tasks } = useQuery<ClientTask[]>({
    queryKey: [`/api/clients/${contact.id}/tasks`],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${contact.id}/tasks`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.ok ? response.json() : [];
    }
  });

  const getContactFullName = (contact: Client) => {
    const firstName = contact.firstName || '';
    const lastName = contact.lastName || '';
    const preferredName = contact.preferredName;
    
    if (preferredName) {
      return `${preferredName} ${lastName}`.trim();
    }
    
    return `${firstName} ${lastName}`.trim() || 'Unknown Contact';
  };

  const getContactInitials = (contact: Client) => {
    const firstName = contact.firstName || '';
    const lastName = contact.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'UN';
  };

  // When toggling edit mode on, snapshot original and clear drafts
  useEffect(() => {
    if (editMode) {
      setOriginalContact(contact);
      setDraft({});
      setCompanyDraft({});
      setPreferencesDraft({});
      setIdentitiesDraft({});
    }
  }, [editMode, contact]);

  const hasContactChanges = Object.keys(draft).length > 0;
  const hasCompanyChanges = Object.keys(companyDraft).length > 0;
  const hasPreferencesChanges = Object.keys(preferencesDraft).length > 0;
  const hasIdentitiesChanges = Object.keys(identitiesDraft).some((k) => Object.keys(identitiesDraft[Number(k)] || {}).length > 0);
  const hasMethodsChanges = !!methodsDraft; // coarse; child emits for any edit interaction
  const hasChanges = hasContactChanges || hasCompanyChanges || hasPreferencesChanges || hasIdentitiesChanges || hasMethodsChanges;

  const handleFieldDraft = (field: keyof Client, value: any) => {
    // Stage draft only; do not mutate contact while editing to avoid UI flips
    setDraft((d) => ({ ...d, [field]: value }));
  };

  const handleCompanyDraft = (updates: any) => {
    setCompanyDraft((prev: any) => ({ ...prev, ...updates }));
  };

  const handlePreferencesDraft = (updates: any) => {
    setPreferencesDraft((prev: any) => ({ ...prev, ...updates }));
  };

  const handleMethodsDraft = (draft: any) => {
    setMethodsDraft(draft);
  };

  const handleIdentityDraft = (id: number, updates: any) => {
    setIdentitiesDraft((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...updates } }));
  };

  const queryClient = useQueryClient();

  const handleSaveAll = async () => {
    if (!hasChanges) return;
    setSavingAll(true);
    try {
      // Save core contact
      if (hasContactChanges) {
        await fetch(`/api/clients/${contact.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify(draft)
        }).then((r) => { if (!r.ok) throw new Error('Failed to save contact'); });
      }

      // Save company info if any
      if (hasCompanyChanges) {
        // fetch existing company record id if present from our query data
        // companyInfo is available via useQuery above
        // @ts-ignore
        const cid = (Array.isArray(companyInfo) && companyInfo[0]?.id) || undefined;
        if (cid) {
          await fetch(`/api/contact-company-info/${cid}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(companyDraft)
          }).then((r) => { if (!r.ok) throw new Error('Failed to save company info'); });
        }
      }

      // Save preferences if any
      if (hasPreferencesChanges) {
        // @ts-ignore
        const pid = (Array.isArray(preferences) && preferences[0]?.id) || undefined;
        if (pid) {
          await fetch(`/api/contact-preferences/${pid}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(preferencesDraft)
          }).then((r) => { if (!r.ok) throw new Error('Failed to save preferences'); });
        }
      }

      // Save identity updates (multiple)
      if (hasIdentitiesChanges) {
        await Promise.all(Object.entries(identitiesDraft).map(async ([key, updates]) => {
          const id = Number(key);
          if (Object.keys(updates as any).length === 0) return;
          await fetch(`/api/contact-identities/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(updates)
          }).then((r) => { if (!r.ok) throw new Error('Failed to save identity'); });
        }));
      }

      // Save Contact Methods subresource (replace set)
      if (methodsDraft && contact?.id) {
        // Load existing methods to delete
        const [existingPhones, existingEmails] = await Promise.all([
          fetch(`/api/clients/${contact.id}/contact-methods?kind=phone`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }).then(r=> r.ok ? r.json() : []),
          fetch(`/api/clients/${contact.id}/contact-methods?kind=email`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }).then(r=> r.ok ? r.json() : []),
        ]);
        const toDeleteIds = [
          ...existingPhones.map((m: any)=> m.id),
          ...existingEmails.map((m: any)=> m.id),
        ];
        await Promise.all(toDeleteIds.map((id)=> fetch(`/api/contact-methods/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })));

        // Create new phones in order
        const primaryPhoneIdx = Math.max(0, Math.min(methodsDraft.primaryPhoneIndex ?? 0, (methodsDraft.phones?.length || 1) - 1));
        await Promise.all((methodsDraft.phones || []).map((row, idx) => {
          const label = row.type;
          const value = (row.value || '').trim() || null;
          return fetch('/api/contact-methods', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ contactId: contact.id, kind: 'phone', label, value, isPrimary: idx === primaryPhoneIdx, order: idx })
          });
        }));
        // Create new emails in order
        const primaryEmailIdx = Math.max(0, Math.min(methodsDraft.primaryEmailIndex ?? 0, (methodsDraft.emails?.length || 1) - 1));
        await Promise.all((methodsDraft.emails || []).map((row, idx) => {
          const label = row.type;
          const value = (row.value || '').trim() || null;
          return fetch('/api/contact-methods', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ contactId: contact.id, kind: 'email', label, value, isPrimary: idx === primaryEmailIdx, order: idx })
          });
        }));

        // No legacy mirroring; list/header will read from subresource directly
      }

      // Invalidate queries so fresh data shows without a page refresh
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/company`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/identities`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/preferences`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/trips`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/notes`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/emails`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/tasks`], exact: false }),
        queryClient.invalidateQueries({ queryKey: [`/api/clients/${contact.id}/contact-methods`], exact: false }),
      ]);

      // Update the selected contact in-place for immediate UI reflection
      if (hasContactChanges) {
        onContactChange(draft);
      }

      setDraft({});
      setCompanyDraft({});
      setPreferencesDraft({});
      setIdentitiesDraft({});
      setMethodsDraft(null);
      setEditMode(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAll(false);
    }
  };

  const handleCancelAll = () => {
    if (originalContact) {
      onContactChange({ ...originalContact });
    }
    setDraft({}); setCompanyDraft({}); setPreferencesDraft({}); setIdentitiesDraft({});
    setEditMode(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sticky top-[var(--global-header-height,35px)] z-20 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b flex items-center gap-4 pl-2 pr-5 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </Button>
        <div className="ml-auto flex gap-2">
          {editMode && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={!hasChanges || savingAll}
                onClick={handleSaveAll}
              >
                {savingAll ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancelAll}>Cancel</Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => setEditMode(v => !v)}>
            {editMode ? 'Done' : 'Edit'}
          </Button>
        </div>
      </div>

      {/* Contact Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              {(contact.avatarUrl || gravatarUrl) ? (
                <AvatarImage src={(contact.avatarUrl || gravatarUrl) as string} alt={`${contact.firstName} ${contact.lastName}`} />
              ) : (
                <AvatarFallback className="bg-orange-100 text-orange-700 text-xl">
                  {getContactInitials(contact)}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1">
              {/* Static name display only; editing moved to Personal tab */}
              <div className="text-2xl font-semibold">
                {(contact.firstName || '') + ' ' + (contact.lastName || '')}
              </div>
              <CardDescription className="text-base">
                {primaryEmail && (
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4" />
                    {primaryEmail}
                  </div>
                )}
                {primaryPhone && (
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="h-4 w-4" />
                    {primaryPhone}
                  </div>
                )}
                {companyName && (
                  <div className="flex items-center gap-2 mt-1">
                    <Building2 className="h-4 w-4" />
                    {companyName}
                  </div>
                )}
              </CardDescription>
            </div>
            <Badge variant={active ? 'default' : 'secondary'} className="mt-1">
              {active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Contact Details Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="relationship" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Relationship</span>
          </TabsTrigger>
          <TabsTrigger value="identity" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Identity</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Ruler className="h-4 w-4" />
            <span className="hidden sm:inline">Preferences</span>
          </TabsTrigger>
          <TabsTrigger value="trips" className="flex items-center gap-2">
            <Luggage className="h-4 w-4" />
            <span className="hidden sm:inline">Trips</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Notes</span>
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Emails</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Tasks</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-6">
          <PersonalInfoPanel 
            contact={contact} 
            editMode={editMode}
            onContactChange={onContactChange}
            onDraftChange={handleFieldDraft}
            onMethodsDraftChange={handleMethodsDraft}
          />
        </TabsContent>

        <TabsContent value="relationship" className="mt-6">
          <RelationshipPanel
            clientId={contact.id}
            companyInfo={(companyInfo && companyInfo.length > 0) ? companyInfo[0] : undefined}
            editMode={editMode}
            onDraftChange={handleCompanyDraft}
          />
        </TabsContent>

        <TabsContent value="identity" className="mt-6">
          <IdentityInfoPanel
            clientId={contact.id}
            identities={identity || []}
            editMode={editMode}
            onDraftChange={handleIdentityDraft}
          />
        </TabsContent>

        <TabsContent value="preferences" className="mt-6">
          <PreferencesPanel
            clientId={contact.id}
            preferences={(preferences && (preferences as any[]).length > 0) ? (preferences as any[])[0] : undefined}
            editMode={editMode}
            onDraftChange={handlePreferencesDraft}
          />
        </TabsContent>

        <TabsContent value="trips" className="mt-6">
          <TripsPanel
            clientId={contact.id}
            trips={trips || []}
          />
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <NotesPanel
            clientId={contact.id}
            notes={notes || []}
          />
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          <EmailsPanel
            clientId={contact.id}
            emails={emails || []}
          />
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <TasksPanel
            clientId={contact.id}
            tasks={tasks || []}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};



// Main Canvas Contacts Component
const CanvasContacts: React.FC = () => {
  const [selectedContact, setSelectedContact] = useState<Client | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [location, navigate] = useLocation();

  const handleContactSelect = (contact: Client) => {
    setSelectedContact(contact);
    setView('detail');
    if (contact?.id) navigate(`/clients/${contact.id}`);
  };

  const handleBackToList = () => {
    setSelectedContact(null);
    setView('list');
    navigate('/clients');
  };

  // Handle deep-linking: /clients/:id
  useEffect(() => {
    const path = location || window.location.pathname;
    const match = path.match(/^\/clients\/(\d+)$/);
    const id = match ? Number(match[1]) : null;
    if (id && (!selectedContact || selectedContact.id !== id)) {
      (async () => {
        try {
          const resp = await fetch(`/api/clients/${id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data) {
              setSelectedContact(data);
              setView('detail');
            }
          }
        } catch (e) {
          // fail silently; stays on list
        }
      })();
    }
    if (!id && view === 'detail' && selectedContact) {
      // Navigated back to /clients; ensure list view
      setView('list');
      setSelectedContact(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <div className="h-full bg-white dark:bg-gray-900">
      <div className="h-full overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {view === 'list' ? (
            <ContactsList 
              onContactSelect={handleContactSelect}
              selectedContactId={selectedContact?.id}
            />
          ) : (
            selectedContact && (
              <ContactDetail 
                contact={selectedContact}
                onBack={handleBackToList}
                onContactChange={(updates) => {
                  setSelectedContact(prev => prev ? { ...prev, ...updates } as Client : prev);
                }}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CanvasContacts; 

// Small cells that fetch primary methods from subresource
// Using React.memo to prevent unnecessary re-renders and queries
const PrimaryEmailCell = React.memo(({ contactId, index }: { contactId: number; index?: number }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  
  // Stagger fetching to prevent overwhelming the server
  useEffect(() => {
    // Fetch immediately for first 5 contacts, then stagger the rest
    const delay = index !== undefined && index < 5 ? 0 : (index || 0) * 100;
    const timer = setTimeout(() => setShouldFetch(true), delay);
    return () => clearTimeout(timer);
  }, [index]);
  
  const { data } = useQuery<any[]>({
    queryKey: [`/api/clients/${contactId}/contact-methods`, 'email', 'primary-cell'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contactId}/contact-methods?kind=email`, { 
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include'
      });
      const list = resp.ok ? await resp.json() : [];
      return Array.isArray(list) ? list : [];
    },
    enabled: shouldFetch,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
  const primary = (data || []).find(m => m.isPrimary) || (data || [])[0];
  return (
    <TableCell>
      <div className="flex items-center text-gray-600">
        <Mail className="h-3 w-3 mr-2" />
        {!shouldFetch ? <span className="text-gray-400">...</span> : primary?.value || 'No email'}
      </div>
    </TableCell>
  );
});

const PrimaryPhoneCell = React.memo(({ contactId, index }: { contactId: number; index?: number }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  
  // Stagger fetching to prevent overwhelming the server
  useEffect(() => {
    // Fetch immediately for first 5 contacts, then stagger the rest
    const delay = index !== undefined && index < 5 ? 0 : (index || 0) * 100;
    const timer = setTimeout(() => setShouldFetch(true), delay);
    return () => clearTimeout(timer);
  }, [index]);
  
  const { data } = useQuery<any[]>({
    queryKey: [`/api/clients/${contactId}/contact-methods`, 'phone', 'primary-cell'],
    queryFn: async () => {
      const resp = await fetch(`/api/clients/${contactId}/contact-methods?kind=phone`, { 
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include'
      });
      const list = resp.ok ? await resp.json() : [];
      return Array.isArray(list) ? list : [];
    },
    enabled: shouldFetch,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
  const primary = (data || []).find(m => m.isPrimary) || (data || [])[0];
  return (
    <TableCell>
      <div className="flex items-center text-gray-600">
        <Phone className="h-3 w-3 mr-2" />
        {!shouldFetch ? <span className="text-gray-400">...</span> : primary?.value || 'No phone'}
      </div>
    </TableCell>
  );
});
